import type { Client, Receivable, Sale } from "@/types";
import { formatCOP, formatDate, formatNumber } from "@/utils/format";
import {
  buildClientTopProducts,
  receivableBalance,
  receivablePaid,
} from "@/utils/accounting";

// ============================================================================
// Ficha de cliente imprimible / exportable a PDF.
// Arma un documento HTML limpio en un iframe oculto y lanza el diálogo de
// impresión del navegador (desde ahí el usuario elige "Guardar como PDF").
// ============================================================================

export interface ClientFichaInput {
  client: Client;
  sales: Sale[]; // solo las compras de ese cliente
  receivables: Receivable[]; // solo las cuentas por cobrar de ese cliente
}

const BRAND = "Amway Tienda Digital Domicilios";

const escapeHtml = (value: string): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const todayLong = (): string =>
  new Date().toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

const buildBuyerRows = (sales: Sale[]): string => {
  if (sales.length === 0) {
    return `<tr><td colspan="5" class="empty">Sin compras registradas.</td></tr>`;
  }
  return sales
    .map(
      (s) => `<tr>
        <td>${escapeHtml(formatDate(s.date))}</td>
        <td class="strong">${escapeHtml(s.productName)}${s.historical ? ' <span class="tag">Histórico</span>' : ""}</td>
        <td class="center">${formatNumber(s.qty)}</td>
        <td class="right">${escapeHtml(formatCOP(s.total))}</td>
        <td class="center">${escapeHtml(s.origin)}</td>
      </tr>`
    )
    .join("");
};

const buildTopRows = (sales: Sale[]): string => {
  const top = buildClientTopProducts(sales).slice(0, 6);
  if (top.length === 0) return "";
  return top
    .map(
      (p, i) => `<tr>
        <td class="center rank">${i + 1}</td>
        <td class="strong">${escapeHtml(p.productName)}</td>
        <td class="center">${formatNumber(p.qty)} und</td>
        <td class="center">${formatNumber(p.purchases)} ${p.purchases === 1 ? "compra" : "compras"}</td>
        <td class="right">${escapeHtml(formatCOP(p.total))}</td>
      </tr>`
    )
    .join("");
};

const buildReceivableBlocks = (receivables: Receivable[]): string => {
  if (receivables.length === 0) {
    return `<p class="muted">Sin saldos por cobrar asociados a este cliente.</p>`;
  }
  return receivables
    .map((r) => {
      const paid = receivablePaid(r);
      const balance = receivableBalance(r);
      const settled = balance <= 0;
      const payments =
        r.payments.length > 0
          ? `<table class="payments">
              <thead><tr><th class="center">#</th><th>Fecha</th><th class="right">Monto</th></tr></thead>
              <tbody>
                ${r.payments
                  .map(
                    (p, idx) => `<tr>
                      <td class="center">${idx + 1}</td>
                      <td>${p.date ? escapeHtml(formatDate(p.date)) : "Sin fecha"}</td>
                      <td class="right strong">${escapeHtml(formatCOP(p.amount))}</td>
                    </tr>`
                  )
                  .join("")}
              </tbody>
            </table>`
          : `<p class="muted small">Todavía no hay abonos registrados.</p>`;

      return `<div class="recv">
        <div class="recv-head">
          <div>
            <p class="recv-title">${escapeHtml(r.productName)}</p>
            <p class="muted small">${escapeHtml(formatDate(r.date))}</p>
          </div>
          <span class="badge ${settled ? "ok" : "pend"}">${settled ? "Pagado" : "Pendiente"}</span>
        </div>
        <table class="recv-sums">
          <tbody>
            <tr>
              <td><span class="muted small">Valor inicial</span><br/><strong>${escapeHtml(formatCOP(r.initialValue))}</strong></td>
              <td><span class="muted small">Abonado</span><br/><strong>${escapeHtml(formatCOP(paid))}</strong></td>
              <td><span class="muted small">Saldo</span><br/><strong>${escapeHtml(formatCOP(balance))}</strong></td>
            </tr>
          </tbody>
        </table>
        ${payments}
      </div>`;
    })
    .join("");
};

const buildHtml = ({ client, sales, receivables }: ClientFichaInput): string => {
  const totalBilled = sales.reduce((a, s) => a + s.total, 0);
  const totalBalance = receivables.reduce((a, r) => a + receivableBalance(r), 0);
  const location =
    [client.neighborhood, client.city, client.department, client.address]
      .filter(Boolean)
      .join(" · ") || "Sin ubicación";

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Ficha · ${escapeHtml(client.name)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: "Inter", Arial, Helvetica, sans-serif;
    color: #1b2431;
    margin: 0;
    padding: 32px;
    font-size: 12px;
    line-height: 1.45;
  }
  h1, h2, h3 { font-family: "Sora", "Inter", Arial, sans-serif; margin: 0; }
  .header {
    display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 3px solid #002F6C; padding-bottom: 14px; margin-bottom: 18px;
  }
  .brand { display: flex; align-items: center; gap: 12px; }
  .logo {
    width: 44px; height: 44px; border-radius: 10px;
    background: #002F6C; color: #7FD4F5; font-weight: 800; font-size: 18px;
    display: flex; align-items: center; justify-content: center; letter-spacing: -0.5px;
  }
  .brand-name { font-size: 16px; font-weight: 700; color: #002F6C; }
  .brand-sub { font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #0089bd; font-weight: 600; }
  .doc-title { text-align: right; }
  .doc-title h2 { font-size: 18px; color: #002F6C; }
  .doc-title p { margin: 3px 0 0; font-size: 11px; color: #6b7280; }
  .client-name { font-size: 22px; font-weight: 800; color: #0f1a2b; margin: 2px 0 2px; }
  .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin: 14px 0 4px; }
  .field { background: #f2f6fb; border: 1px solid #dbe6f2; border-radius: 8px; padding: 8px 10px; }
  .field .k { font-size: 9px; text-transform: uppercase; letter-spacing: .5px; color: #7c8aa0; font-weight: 700; }
  .field .v { font-size: 12px; font-weight: 600; color: #16233a; }
  .full { grid-column: 1 / -1; }
  .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 12px 0 8px; }
  .stat { border: 1px solid #dbe6f2; border-radius: 8px; padding: 10px 12px; }
  .stat .k { font-size: 9px; text-transform: uppercase; letter-spacing: .5px; color: #7c8aa0; font-weight: 700; }
  .stat .v { font-size: 16px; font-weight: 800; color: #002F6C; }
  section { margin-top: 18px; }
  section > h3 {
    font-size: 13px; color: #002F6C; padding-bottom: 5px; margin-bottom: 8px;
    border-bottom: 1px solid #dbe6f2;
  }
  table { width: 100%; border-collapse: collapse; }
  thead th {
    text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: .5px;
    color: #7c8aa0; padding: 6px 8px; border-bottom: 1px solid #dbe6f2;
  }
  tbody td { padding: 7px 8px; border-bottom: 1px solid #eef2f7; vertical-align: top; }
  .center { text-align: center; }
  .right { text-align: right; }
  .strong { font-weight: 600; color: #16233a; }
  .rank { font-weight: 800; color: #0089bd; width: 24px; }
  .tag {
    display: inline-block; font-size: 8px; font-weight: 700; padding: 1px 6px;
    border-radius: 999px; background: #e6f0fb; color: #1f5aa8; margin-left: 4px;
  }
  .muted { color: #7c8aa0; }
  .small { font-size: 10px; }
  .empty { text-align: center; color: #7c8aa0; padding: 14px; }
  .recv { border: 1px solid #dbe6f2; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; }
  .recv-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
  .recv-title { font-weight: 700; color: #16233a; margin: 0; }
  .recv-sums { margin: 8px 0; }
  .recv-sums td { border: none; padding: 2px 0; }
  .badge { font-size: 9px; font-weight: 700; padding: 3px 9px; border-radius: 999px; white-space: nowrap; }
  .badge.ok { background: #e2f5ec; color: #1c7a4d; }
  .badge.pend { background: #fdeede; color: #a85a10; }
  .payments { margin-top: 6px; border-top: 1px dashed #e2e8f0; }
  .payments thead th { padding-top: 8px; }
  .footer {
    margin-top: 26px; padding-top: 10px; border-top: 1px solid #dbe6f2;
    font-size: 10px; color: #8a97ab; display: flex; justify-content: space-between;
  }
  @media print {
    body { padding: 18px; }
    .recv, .field, .stat { break-inside: avoid; }
    tr { break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <div class="logo">ATT</div>
      <div>
        <div class="brand-name">${escapeHtml(BRAND)}</div>
        <div class="brand-sub">Domicilios · ATDD</div>
      </div>
    </div>
    <div class="doc-title">
      <h2>Ficha de cliente</h2>
      <p>Generada el ${escapeHtml(todayLong())}</p>
    </div>
  </div>

  <h1 class="client-name">${escapeHtml(client.name)}</h1>

  <div class="grid">
    <div class="field"><div class="k">Cédula</div><div class="v">${escapeHtml(client.cedula || "Sin cédula")}</div></div>
    <div class="field"><div class="k">Celular</div><div class="v">${escapeHtml(client.phone || "—")}</div></div>
    <div class="field full"><div class="k">Ubicación</div><div class="v">${escapeHtml(location)}</div></div>
  </div>

  <div class="stats">
    <div class="stat"><div class="k">Compras</div><div class="v">${formatNumber(sales.length)}</div></div>
    <div class="stat"><div class="k">Total facturado</div><div class="v">${escapeHtml(formatCOP(totalBilled))}</div></div>
    <div class="stat"><div class="k">Saldo en cartera</div><div class="v">${escapeHtml(formatCOP(totalBalance))}</div></div>
  </div>

  ${
    sales.length > 0
      ? `<section>
          <h3>Productos que más compra</h3>
          <table>
            <thead><tr><th class="center">#</th><th>Producto</th><th class="center">Unidades</th><th class="center">Frecuencia</th><th class="right">Total</th></tr></thead>
            <tbody>${buildTopRows(sales)}</tbody>
          </table>
        </section>`
      : ""
  }

  <section>
    <h3>Historial de compras (${sales.length})</h3>
    <table>
      <thead><tr><th>Fecha</th><th>Producto</th><th class="center">Cant.</th><th class="right">Total</th><th class="center">Canal</th></tr></thead>
      <tbody>${buildBuyerRows(sales)}</tbody>
    </table>
  </section>

  <section>
    <h3>Cartera y abonos</h3>
    ${buildReceivableBlocks(receivables)}
  </section>

  <div class="footer">
    <span>${escapeHtml(BRAND)}</span>
    <span>Ficha interna · ${escapeHtml(client.name)}</span>
  </div>
</body>
</html>`;
};

// Imprime la ficha del cliente usando un iframe oculto. Desde el diálogo de
// impresión del navegador se puede "Guardar como PDF".
export const printClientFicha = (input: ClientFichaInput): void => {
  const html = buildHtml(input);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    document.body.removeChild(iframe);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = () => {
    if (iframe.parentNode) document.body.removeChild(iframe);
  };

  // Damos un instante para que el navegador pinte el documento antes de imprimir.
  window.setTimeout(() => {
    try {
      win.focus();
      win.print();
    } finally {
      window.setTimeout(cleanup, 1500);
    }
  }, 350);
};